import React, { useMemo } from 'react'

import FormItem from '../FormItem'

import type { IFormItemProps } from '../FormItem'

type ChangeType<T, K = HTMLElement> = (value: T) => void | React.HTMLAttributes<K>['onChange']

type ItemPropsType<T, K = HTMLElement> = {
  value: T
  onChange: ChangeType<T, K>
}

interface FormInputProps<T, K = HTMLElement> extends Omit<IFormItemProps, 'name'> {
  value?: T
  onChange?: ChangeType<T, K>
  children?: (value: T, onChange: ChangeType<T, K>) => React.ReactElement
}

/** form 输入组件 */
function FormInput<T, K = HTMLElement>({ value, onChange, children }: FormInputProps<T, K>): React.ReactElement {
  return <>{children?.(value, onChange)}</>
}

interface SerialFormItemProps<T, K = HTMLElement> extends Omit<IFormItemProps, 'name'> {
  names: IFormItemProps['name'][]
  itemsProps?: ItemPropsType<T, K>[]
  children?: (itemsProps: ItemPropsType<T, K>[]) => React.ReactElement
}

/** 串行formItem组件，用于同时在一个fc方法中获取、更新一个或多个表单项value */
/** onChange 才能触发表单校验及formLayout编辑状态变更 */
function SerialFormItem<T = string, K = HTMLElement>({ names, itemsProps, children, ...rest }: SerialFormItemProps<T, K>): React.ReactElement {
  const [name, nameList] = useMemo(() => {
    const list = [...names]
    const [current] = list.splice(0, 1)
    return [current, list]
  }, [names])

  return (
    <>
      {name ? (
        <FormItem name={name} {...rest}>
          <FormInput<T, K>>
            {(value, onChange) => {
              const newItemProps = [
                ...(itemsProps || []),
                {
                  value,
                  onChange,
                } as ItemPropsType<T, K>,
              ]
              return (
                <>
                  {nameList?.length ? (
                    <SerialFormItem<T, K> {...rest} names={nameList} itemsProps={newItemProps} noStyle style={{ marginBottom: 0 }}>
                      {children}
                    </SerialFormItem>
                  ) : (
                    children?.(newItemProps)
                  )}
                </>
              )
            }}
          </FormInput>
        </FormItem>
      ) : null}
    </>
  )
}

export default SerialFormItem
